/**
 * The print document's stylesheet, copied from production's three
 * `*-report-print.tsx` pages at `11ca09a`.
 *
 * **Production's print output is a paginated document, not the screen report
 * with margins.** It lays out Letter-sized pages with an explicit break after
 * each, and paints them on white with colour-adjust forced on so the score
 * bars and grade chips survive the print pipeline. V2 previously reused the
 * on-screen report and asked the browser for sensible margins, which produced
 * one long reflowed column.
 *
 * The class names are production's (`page`, `page.cover`, `page-label`,
 * `page-title`, `print-bar`) so the two documents can be compared side by side.
 */
export const PRINT_PAGE_CSS = `
.page {
  background: #fff;
  color: #14110c;
  border: 1px solid #e5e0d8;
  border-radius: 6px;
  padding: 48px 56px;
  margin-bottom: 24px;
  box-sizing: border-box;
}
.page.cover { padding: 56px 64px; }
.page-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8a7f6d;
  margin-bottom: 8px;
}
.page-title {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 20px;
  color: #14110c;
}
.print-page-footer {
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a7f6d;
  padding: 16px 0 32px;
}

@page { size: Letter; margin: 0; }

@media print {
  html, body {
    width: 8.5in;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Production hides both of these on paper. */
  .print-bar { display: none !important; }
  .print-page-footer { display: none !important; }

  .print-document {
    width: 8.5in;
    margin: 0 !important;
    padding: 0 !important;
  }

  .page {
    width: 8.5in;
    min-height: 11in;
    box-sizing: border-box;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    page-break-after: always;
    break-after: page;
    padding: 64px 72px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  .page.cover,
  .page.final-page { padding: 72px 80px; }

  /* No trailing blank sheet. */
  .page:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .card, .score-card, .two-col, .stop-card, .start-card, .truth-card,
  .reassess-box, .final-step, .break-inside-avoid {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /*
   * Descendant hardening (visual system H.1), kept from the previous sheet and
   * still required. The page paints itself dark-on-white, but that only reaches
   * elements which INHERIT their colour; a descendant carrying its own colour
   * utility beats an !important declaration on an ancestor. The print document
   * uses the same text-muted-foreground / bg-card tokens as the screen, so
   * without this rule the output is white-on-white and dark-on-dark.
   * (No backticks in this comment: it lives inside a template literal.)
   *
   * Borders are left alone: they resolve to --border, which is legible on
   * paper. Backgrounds are cleared wholesale, which is safe here because the
   * print document paints no fills of its own -- unlike the screen report, it
   * has no score bars or grade chips to preserve.
   */
  body * { color: #111 !important; background-color: transparent !important; }
  a { color: #111 !important; text-decoration: none !important; }
  .print\:hidden { display: none !important; }
}
`;

/** Trigger the browser print dialog (injectable for tests). */
export function printReport(print: () => void = () => window.print()): void {
  print();
}
